import { KeyboardEvent, useRef } from "react";
import styled, { css } from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";

const { color, density, radius, typeScale } = adminTokens;
// The design's segmented controls pad their pill 3px and set the segments 2px
// apart, with 18px at each side of a label.
const containerPadding = 3;
const segmentGap = 2;
const labelPadding = 18;
// On a phone the control spans its container and each segment is 38px, so the
// control clears the 44px hit target (PHONE-R6, PHONE-R7).
const phoneSegmentHeight = 38;

export type SegmentedControlKind = "tabs" | "radios";
export type SegmentedControlSurface = "white" | "canvas";

interface SegmentStyleProps {
  $isSelected: boolean;
  $surface: SegmentedControlSurface;
  $isPhone: boolean;
}

const Container = styled.div<{ $surface: SegmentedControlSurface; $isPhone: boolean }>`
  display: ${({ $isPhone }) => ($isPhone ? "flex" : "inline-flex")};
  ${({ $isPhone }) =>
    $isPhone &&
    css`
      width: 100%;
    `}
  gap: ${segmentGap}px;
  box-sizing: border-box;
  padding: ${containerPadding}px;
  border: ${({ $surface }) => ($surface === "white" ? `1px solid ${color.border}` : "0")};
  border-radius: ${radius.pill}px;
  background-color: ${({ $surface }) => ($surface === "white" ? color.surface : color.canvas)};
`;

const selectedFill = ({ $surface }: SegmentStyleProps) =>
  $surface === "white" ? color.canvas : color.surface;

const labelColor = ({ $isSelected, $surface }: SegmentStyleProps) =>
  $surface === "canvas" && !$isSelected ? color.inkSoft : color.navy;

const Segment = styled.button<SegmentStyleProps>`
  ${({ $isPhone }) =>
    $isPhone &&
    css`
      flex: 1;
    `}
  box-sizing: border-box;
  height: ${({ $isPhone }) => ($isPhone ? phoneSegmentHeight : density.minimumHitTarget)}px;
  padding: 0 ${labelPadding}px;
  border: 0;
  border-radius: ${radius.pill}px;
  background-color: ${(props) => (props.$isSelected ? selectedFill(props) : "transparent")};
  color: ${labelColor};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${typeScale.labelValue.fontSize};
  line-height: ${typeScale.labelValue.lineHeight};
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible {
    ${focusOutline}
  }

  &:disabled {
    color: ${color.disabledText};
    cursor: not-allowed;
  }
`;

export interface SegmentedControlOption {
  // Unique among the options: it keys the segment.
  value: string;
  label: string;
  isDisabled?: boolean;
  // For a tab: the id of the panel it controls (SEG-R21).
  panelId?: string;
}

export interface SegmentedControlProps {
  label: string;
  kind: SegmentedControlKind;
  options: SegmentedControlOption[];
  value: string;
  onChange: (value: string) => void;
  surface?: SegmentedControlSurface;
}

const roles = {
  tabs: { group: "tablist", segment: "tab" },
  radios: { group: "radiogroup", segment: "radio" },
} as const;

// Picks one of a few options and shows the choice at once (SEG-R1…R22). The
// segment that is selected is the only one Tab reaches; the arrow keys, Home and
// End move the selection among the segments that are not disabled, starting from
// the segment that has the focus.
export const SegmentedControl = ({
  label,
  kind,
  options,
  value,
  onChange,
  surface = "white",
}: SegmentedControlProps) => {
  const isPhone = useIsPhoneLayout();
  const segmentRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { group, segment } = roles[kind];
  const enabledValues = options
    .filter((option) => !option.isDisabled)
    .map((option) => option.value);
  const reachableValue = enabledValues.includes(value) ? value : enabledValues[0];

  const select = (nextValue: string) => {
    segmentRefs.current[nextValue]?.focus();
    if (nextValue !== value) onChange(nextValue);
  };

  const targetFor = (key: string, fromValue: string): string | undefined => {
    const index = enabledValues.indexOf(fromValue);
    const lastIndex = enabledValues.length - 1;
    const next = enabledValues[index === lastIndex ? 0 : index + 1];
    const previous = enabledValues[index <= 0 ? lastIndex : index - 1];

    switch (key) {
      case "ArrowRight":
        return next;
      case "ArrowLeft":
        return previous;
      case "ArrowDown":
        return kind === "radios" ? next : undefined;
      case "ArrowUp":
        return kind === "radios" ? previous : undefined;
      case "Home":
        return enabledValues[0];
      case "End":
        return enabledValues[lastIndex];
      default:
        return undefined;
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, fromValue: string) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

    const target = targetFor(event.key, fromValue);
    if (target === undefined) return;

    event.preventDefault();
    select(target);
  };

  return (
    <Container role={group} aria-label={label} $surface={surface} $isPhone={isPhone}>
      {options.map((option) => {
        const isSelected = option.value === value;

        return (
          <Segment
            key={option.value}
            ref={(element) => {
              segmentRefs.current[option.value] = element;
            }}
            type="button"
            role={segment}
            {...(kind === "tabs"
              ? { "aria-selected": isSelected, "aria-controls": option.panelId }
              : { "aria-checked": isSelected })}
            tabIndex={option.value === reachableValue ? 0 : -1}
            disabled={option.isDisabled}
            $isSelected={isSelected}
            $surface={surface}
            $isPhone={isPhone}
            onClick={() => select(option.value)}
            onKeyDown={(event) => handleKeyDown(event, option.value)}
          >
            {option.label}
          </Segment>
        );
      })}
    </Container>
  );
};
